/**
 * ShareBar — the share sheet for a listing, an open house or an article.
 *
 * Renders wherever one of those does: the listing grid and detail pages,
 * the open-house cards, the article pages and the admin pages, so the
 * owner can share from the same place visitors do.
 *
 * Every network takes the same shape — open its composer with the link
 * prefilled, and let it build the preview card from the backend's Open
 * Graph tags (see SHARE-PREVIEWS.md). The link is a tracked short link
 * minted on the click (`features/social/shareLinks.ts`), and the click
 * itself is counted, so the owner can see which platform brings people
 * back and which of them go on to sign a buyer's agreement.
 *
 * Two things are different on purpose:
 *   - On a phone the device's own share sheet comes first, because that
 *     is where every app the person actually uses lives.
 *   - **Instagram** accepts no shared link from a web page — no
 *     `sharer.php` equivalent exists and the Stories deep link needs a
 *     native app with a Facebook App ID. For a listing it gets a post
 *     kit instead: the caption on the clipboard and the photo in the
 *     Downloads folder. Articles and open houses have no photo to hand
 *     over, so the button only appears for listings.
 */

import { useId, useState, type ReactNode } from 'react';
import { Copy, Download, Home, Loader2, Mail, MessageCircle, MessageSquare, Send, Share2 } from 'lucide-react';
import { Modal } from '@/components/base';
import { notify } from '@/utils/notify';
import { reportShare, resolveShareUrl } from '@/features/social/shareLinks';
import {
    copyLinkToClipboard,
    copyText,
    downloadFile,
    instagramCaption,
    listingPhotoDownloadUrl,
    nativeShare,
    openShareWindow,
    prefersNativeShare,
    shareIntentUrl,
    shareTargetUrl,
    type SharePlatform,
    type ShareTarget,
    type ShareTargetKind,
} from '@/utils/share';

export type ShareBarVariant = 'icon' | 'button' | 'stacked' | 'inline';

export interface ShareBarProps {
    target: ShareTarget;
    /**
     * `icon` is a bare button for tight spots (a card corner); `button`
     * is a labelled one; `stacked` matches the icon-over-caption column
     * on the public listing page; `inline` lays the whole sheet out in
     * place, for the foot of an article.
     */
    variant?: ShareBarVariant;
    /** Visible text on the `button` and `stacked` triggers. */
    label?: string;
    /** Screen-reader name of the trigger; defaults to the sheet title ("Share this listing"). */
    ariaLabel?: string;
    /** Where on the site the bar sits — recorded with each share event. Defaults to the variant. */
    placement?: string;
    className?: string;
}

const SHEET_TITLES: Record<ShareTargetKind, string> = {
    listing: 'Share this listing',
    open_house: 'Share this open house',
    post: 'Share this article',
};

const DEFAULT_LABEL = 'Share';
const LINK_COPIED_MESSAGE = 'Link copied';
const WINDOW_BLOCKED_MESSAGE = 'Your browser blocked the window.';
const INSTAGRAM_FOOTNOTE = "Instagram can't take a link from a web page, so that button builds the post for you.";

/**
 * Brand marks, drawn rather than imported.
 *
 * Lucide dropped its brand icons — `Facebook`, `Instagram` and
 * `Linkedin` import as `undefined` now, which renders as "Element type
 * is invalid" rather than anything visible. The networks without a
 * drawable mark get a letter badge or Lucide's generic glyph: on a
 * brand-coloured circle next to the network's name they read correctly.
 */
function BrandGlyph({ path, size = 20 }: { path: string; size?: number }) {
    return (
        <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
            <path d={path} />
        </svg>
    );
}

function LetterGlyph({ children }: { children: ReactNode }) {
    return <span className="text-sm font-bold leading-none" aria-hidden="true">{children}</span>;
}

const GLYPH_X =
    'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.657l-5.214-6.817-5.966 6.817H1.68l7.73-8.835L1.254 2.25h6.826'
    + 'l4.713 6.231 5.451-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z';

const GLYPH_FACEBOOK =
    'M15.12 5.32H17V2.14A26.11 26.11 0 0 0 14.26 2C11.54 2 9.68 3.66 9.68 6.7v2.62H6.61v3.56h3.07V22h3.68v-9.12h3.06'
    + 'l.46-3.56h-3.52V7.05c0-1.05.29-1.73 1.76-1.73Z';

const GLYPH_LINKEDIN =
    'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414'
    + 'v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926'
    + '-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 '
    + '13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 '
    + '24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z';

const GLYPH_BLUESKY =
    'M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 '
    + '5.65.624 6.479.815 2.736 3.713 3.66 6.383 3.364.136-.02.275-.039.415-.056-.138.022-.276.04-.415.056-3.912.58'
    + '-7.387 2.005-2.83 7.078 5.013 5.19 6.87-1.113 7.823-4.308.953 3.195 2.05 9.271 7.733 4.308 4.267-4.308 1.172'
    + '-6.498-2.74-7.078a8.741 8.741 0 0 1-.415-.056c.14.017.279.036.415.056 2.67.297 5.568-.628 6.383-3.364.246-.828'
    + '.624-5.79.624-6.478 0-.69-.139-1.861-.902-2.206-.659-.298-1.664-.62-4.3 1.24C16.046 4.748 13.087 8.687 12 10.8Z';

const GLYPH_INSTAGRAM =
    'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205'
    + '-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.069-4.85.069-3.204 0-3.584-.012'
    + '-4.849-.069-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149'
    + '-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069Zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2'
    + '-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98'
    + '1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689'
    + '.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0Zm0 5.838a6.162'
    + ' 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324ZM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8Zm6.406-11.845a1.44 1.44 0'
    + ' 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881Z';

interface NetworkOption {
    platform: SharePlatform;
    label: string;
    hint: string;
    icon: ReactNode;
    tone: string;
}

const POST_HINT = 'Opens a post with the link';
const CHAT_HINT = 'Pick a chat to send it to';

/** Every network with a web composer, in the order the sheet lists them. */
const NETWORKS: readonly NetworkOption[] = [
    { platform: 'x', label: 'X', hint: POST_HINT, icon: <BrandGlyph path={GLYPH_X} />, tone: 'bg-black' },
    { platform: 'facebook', label: 'Facebook', hint: POST_HINT, icon: <BrandGlyph path={GLYPH_FACEBOOK} />, tone: 'bg-[#1877F2]' },
    { platform: 'linkedin', label: 'LinkedIn', hint: POST_HINT, icon: <BrandGlyph path={GLYPH_LINKEDIN} />, tone: 'bg-[#0A66C2]' },
    { platform: 'threads', label: 'Threads', hint: POST_HINT, icon: <LetterGlyph>@</LetterGlyph>, tone: 'bg-black' },
    { platform: 'bluesky', label: 'Bluesky', hint: POST_HINT, icon: <BrandGlyph path={GLYPH_BLUESKY} size={18} />, tone: 'bg-[#1185FE]' },
    { platform: 'reddit', label: 'Reddit', hint: 'Starts a post in a subreddit', icon: <LetterGlyph>r/</LetterGlyph>, tone: 'bg-[#FF4500]' },
    { platform: 'whatsapp', label: 'WhatsApp', hint: CHAT_HINT, icon: <MessageCircle size={20} />, tone: 'bg-[#25D366]' },
    { platform: 'telegram', label: 'Telegram', hint: CHAT_HINT, icon: <Send size={18} />, tone: 'bg-[#26A5E4]' },
    { platform: 'pinterest', label: 'Pinterest', hint: 'Pins the photo to a board', icon: <LetterGlyph>P</LetterGlyph>, tone: 'bg-[#E60023]' },
    { platform: 'tumblr', label: 'Tumblr', hint: POST_HINT, icon: <LetterGlyph>t</LetterGlyph>, tone: 'bg-[#36465D]' },
    { platform: 'nextdoor', label: 'Nextdoor', hint: 'Shares it with your neighbors', icon: <Home size={18} />, tone: 'bg-[#3B7A0E]' },
    { platform: 'email', label: 'Email', hint: 'Opens your mail app', icon: <Mail size={18} />, tone: 'bg-gray-700' },
    { platform: 'sms', label: 'Text message', hint: 'Opens your messages app', icon: <MessageSquare size={18} />, tone: 'bg-emerald-600' },
];

const NATIVE_OPTION: NetworkOption = {
    platform: 'other',
    label: 'Share…',
    hint: "Your device's share sheet",
    icon: <Share2 size={18} />,
    tone: 'bg-primary',
};

const INSTAGRAM_OPTION: NetworkOption = {
    platform: 'instagram',
    label: 'Instagram',
    hint: 'Copies the caption and saves the photo',
    icon: <BrandGlyph path={GLYPH_INSTAGRAM} />,
    tone: 'bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF]',
};

interface PlatformButtonProps {
    option: NetworkOption;
    busy: boolean;
    onClick: () => void;
}

function PlatformButton({ option, busy, onClick }: PlatformButtonProps) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={busy}
            className="flex w-full items-center gap-3 rounded-xl border border-gray-200 px-4 py-3 text-left transition hover:bg-gray-50 disabled:opacity-60"
        >
            <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white ${option.tone}`}>
                {busy ? <Loader2 size={18} className="animate-spin" /> : option.icon}
            </span>
            <span className="min-w-0">
                <span className="block font-medium text-gray-900">{option.label}</span>
                <span className="block text-xs text-gray-500">{option.hint}</span>
            </span>
        </button>
    );
}

/** Report the Instagram kit's two steps honestly — a silent failure leaves someone pasting nothing. */
function notifyInstagramKit(copied: boolean, saved: boolean): void {
    if (copied && saved) {
        notify.success('Caption copied, photo saved', {
            description: 'Open Instagram, add the photo and paste the caption.',
        });
    } else if (copied) {
        notify.warning('Caption copied, but the photo could not be saved', {
            description: 'Save it from the gallery instead.',
        });
    } else if (saved) {
        notify.warning('Photo saved, but the caption could not be copied', {
            description: 'Copy the link below and write your own caption.',
        });
    } else {
        notify.error('Could not build the Instagram post');
    }
}

/** A composer the browser refused to open; the sheet offers it as a plain link instead. */
interface BlockedComposer {
    platform: SharePlatform;
    label: string;
    href: string;
}

const networkLabel = (platform: SharePlatform): string =>
    NETWORKS.find((option) => option.platform === platform)?.label ?? platform;

interface ShareSheetProps {
    target: ShareTarget;
    placement: string;
    /** Called after a share went out, so a modal can close itself. */
    onShared?: () => void;
}

/** The sheet itself: the platform grid and the copyable link. */
function ShareSheet({ target, placement, onShared }: ShareSheetProps) {
    const [busy, setBusy] = useState<SharePlatform | null>(null);
    const [blockedComposer, setBlockedComposer] = useState<BlockedComposer | null>(null);
    // Decided once: the device does not grow a share sheet mid-session.
    const [nativeAvailable] = useState(prefersNativeShare);
    const linkFieldId = useId();
    const displayUrl = shareTargetUrl(target);

    /**
     * One path for every button: resolve the tracked link, hand it to
     * the platform, and count the share only when it actually went out.
     */
    const share = async (
        platform: SharePlatform,
        send: (url: string) => Promise<boolean> | boolean,
        { closeAfter = true } = {},
    ) => {
        setBusy(platform);
        setBlockedComposer(null);
        try {
            const url = await resolveShareUrl(target, platform);
            if (!(await send(url))) return;
            reportShare(target, platform, placement);
            if (closeAfter) onShared?.();
        } finally {
            setBusy(null);
        }
    };

    /**
     * A popup blocker (Safari's, after the awaited mint) leaves nothing
     * open: the sheet stays, nothing is counted, and the composer is
     * offered as a plain link — a tap on that is a user gesture no
     * browser refuses.
     */
    const openComposer = (platform: SharePlatform) => share(platform, (url) => {
        const intent = shareIntentUrl(platform, url, target.caption, target.title);
        if (!intent) return false;
        if (openShareWindow(intent, platform)) return true;
        setBlockedComposer({ platform, label: networkLabel(platform), href: intent });
        return false;
    });

    const shareByBlockedLink = (blocked: BlockedComposer) => {
        reportShare(target, blocked.platform, placement);
        setBlockedComposer(null);
        onShared?.();
    };

    const shareNatively = () => share('other', (url) => nativeShare({ title: target.title, text: target.caption, url }));

    const copyLink = () => share('copy', (url) => {
        copyLinkToClipboard(url, LINK_COPIED_MESSAGE);
        return true;
    }, { closeAfter: false });

    /**
     * Instagram takes no link, so build the post instead: caption on the
     * clipboard, photo in Downloads. Either step succeeding is a share
     * worth counting.
     */
    const buildInstagramPost = () => share('instagram', async (url) => {
        if (!target.listing) return false;
        const { facts, photoIndex } = target.listing;
        const copied = await copyText(instagramCaption(facts, url));
        const saved = await downloadFile(listingPhotoDownloadUrl(facts.listingId, photoIndex), `${facts.listingId}.jpg`);
        notifyInstagramKit(copied, saved);
        return copied || saved;
    }, { closeAfter: false });

    return (
        <div>
            <p className="mb-4 text-sm text-gray-600">{target.caption}</p>

            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {nativeAvailable && (
                    <div className="sm:col-span-2">
                        <PlatformButton option={NATIVE_OPTION} busy={busy === 'other'} onClick={() => void shareNatively()} />
                    </div>
                )}
                {NETWORKS.map((option) => (
                    <PlatformButton
                        key={option.platform}
                        option={option}
                        busy={busy === option.platform}
                        onClick={() => void openComposer(option.platform)}
                    />
                ))}
                {target.listing && (
                    <PlatformButton option={INSTAGRAM_OPTION} busy={busy === 'instagram'} onClick={() => void buildInstagramPost()} />
                )}
            </div>

            {blockedComposer && (
                <p role="status" className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    {WINDOW_BLOCKED_MESSAGE}{' '}
                    <a
                        href={blockedComposer.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => shareByBlockedLink(blockedComposer)}
                        className="font-semibold underline"
                    >
                        Open {blockedComposer.label} anyway
                    </a>
                </p>
            )}

            <div className="mt-5 border-t border-gray-100 pt-4">
                <label className="mb-1 block text-xs font-medium text-gray-600" htmlFor={linkFieldId}>
                    Link
                </label>
                <div className="flex gap-2">
                    <input
                        id={linkFieldId}
                        type="text"
                        readOnly
                        value={displayUrl}
                        onFocus={(e) => e.currentTarget.select()}
                        className="w-full rounded-md border border-gray-300 px-2 py-1.5 font-mono text-xs text-gray-700"
                    />
                    <button
                        type="button"
                        onClick={() => void copyLink()}
                        disabled={busy === 'copy'}
                        className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                    >
                        {busy === 'copy' ? <Loader2 size={14} className="animate-spin" /> : <Copy size={14} />}
                        Copy
                    </button>
                </div>
                {target.listing && (
                    <p className="mt-2 flex items-center gap-1.5 text-[11px] text-gray-400">
                        <Download size={12} />
                        {INSTAGRAM_FOOTNOTE}
                    </p>
                )}
            </div>
        </div>
    );
}

export function ShareBar({
    target,
    variant = 'button',
    label = DEFAULT_LABEL,
    ariaLabel,
    placement = variant,
    className = '',
}: ShareBarProps) {
    const [isOpen, setIsOpen] = useState(false);
    const title = SHEET_TITLES[target.kind];
    const triggerName = ariaLabel ?? title;

    if (variant === 'inline') {
        return (
            <section className={`rounded-2xl bg-white p-6 shadow-sm ${className}`} aria-label={title}>
                <h2 className="mb-1 text-lg font-semibold text-gray-900">{title}</h2>
                <ShareSheet target={target} placement={placement} />
            </section>
        );
    }

    const trigger = {
        icon: (
            <button
                type="button"
                onClick={() => setIsOpen(true)}
                aria-label={triggerName}
                title={triggerName}
                className={`flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-gray-700 shadow transition hover:bg-white hover:text-gray-900 ${className}`}
            >
                <Share2 size={16} />
            </button>
        ),
        button: (
            <button
                type="button"
                onClick={() => setIsOpen(true)}
                aria-label={triggerName}
                className={`flex items-center gap-2 rounded-lg px-4 py-2 text-left text-gray-700 transition-colors hover:bg-gray-100 ${className}`}
            >
                <Share2 size={18} />
                {label}
            </button>
        ),
        stacked: (
            <button
                type="button"
                onClick={() => setIsOpen(true)}
                aria-label={triggerName}
                className={`flex flex-col items-center gap-2 transition hover:opacity-70 ${className}`}
            >
                <Share2 size={24} className="text-gray-800" />
                <span className="text-sm font-medium uppercase text-gray-900">{label}</span>
            </button>
        ),
    }[variant];

    return (
        // Everything is wrapped because the trigger often sits inside a
        // <Link> (PropertyCard, the chat cards): an un-stopped click on
        // "Share" would navigate to the listing instead of sharing it.
        // Keyboard users activate the buttons directly, so swallowing
        // the bubbled click here costs them nothing.
        <div
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
            className="contents"
        >
            {trigger}

            <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} title={title} size="md">
                <ShareSheet target={target} placement={placement} onShared={() => setIsOpen(false)} />
            </Modal>
        </div>
    );
}

export default ShareBar;
